/* eslint-disable react/button-has-type, jsx-a11y/label-has-associated-control, react/no-array-index-key, jsx-a11y/no-static-element-interactions, no-unsafe-optional-chaining */

import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";

/* Vodafone corporate colors */
const VF = {
  red: "#E60000",
  redDark: "#BD0000",
  redLight: "#FF2B2B",
  grey900: "#1A1A1A",
  grey700: "#333333",
  grey500: "#666666",
  grey300: "#999999",
  grey100: "#F4F4F4",
  grey50: "#FAFAFA",
  white: "#FFFFFF",
  border: "#E0E0E0",
  borderLight: "#F0F0F0",
  accent: "#00B0CA",
  success: "#00857C",
  warning: "#EB6100",
  shadow: "0 2px 12px rgba(0,0,0,0.08)",
  shadowHover: "0 4px 20px rgba(0,0,0,0.12)",
};

/* react-select custom theme */
const selectCustomStyles = {
  control: (base, state) => ({
    ...base,
    fontFamily: "VodafoneRg-Regular, sans-serif",
    fontSize: "0.9rem",
    minHeight: "44px",
    borderColor: state.isFocused ? VF.red : VF.border,
    boxShadow: state.isFocused ? `0 0 0 2px ${VF.red}33` : "none",
    borderRadius: "8px",
    "&:hover": { borderColor: VF.red },
  }),

  option: (base, state) => ({
    ...base,
    fontFamily: "VodafoneRg-Regular, sans-serif",
    fontSize: "0.875rem",
    backgroundColor: (() => {
      if (state.isSelected) return VF.red;
      if (state.isFocused) return VF.grey100;
      return VF.white;
    })(),
    color: state.isSelected ? VF.white : VF.grey700,
    cursor: "pointer",
    "&:active": {
      backgroundColor: VF.redLight,
      color: VF.white,
    },
  }),

  placeholder: (base) => ({
    ...base,
    color: VF.grey300,
    fontFamily: "VodafoneRg-Regular, sans-serif",
  }),

  singleValue: (base) => ({
    ...base,
    color: VF.grey900,
    fontFamily: "VodafoneRg-Regular, sans-serif",
  }),

  menu: (base) => ({
    ...base,
    borderRadius: "8px",
    boxShadow: VF.shadowHover,
    zIndex: 10,
  }),

  menuList: (base) => ({
    ...base,
    maxHeight: "220px",
  }),
};

const styles = {
  container: {
    padding: "28px 32px",
    borderRadius: "12px",
    background: VF.white,
    maxWidth: "1080px",
    margin: "2rem auto",
    fontFamily: "VodafoneRg-Regular, sans-serif",
    border: `1px solid ${VF.border}`,
    boxShadow: VF.shadow,
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
    borderBottom: `2px solid ${VF.red}`,
    paddingBottom: "12px",
  },

  heading: {
    fontFamily: "VodafoneRg-Bold, sans-serif",
    fontSize: "1.5rem",
    color: VF.grey900,
    margin: 0,
  },

  badge: {
    background: VF.red,
    color: VF.white,
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "0.75rem",
    fontFamily: "VodafoneRg-Bold, sans-serif",
  },

  filters: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr auto",
    gap: "16px",
    alignItems: "end",
    marginBottom: "24px",
  },

  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  filterLabel: {
    fontFamily: "VodafoneRg-Bold, sans-serif",
    fontSize: "0.8rem",
    color: VF.grey500,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  assignBtn: {
    fontFamily: "VodafoneRg-Bold, sans-serif",
    fontSize: "0.875rem",
    padding: "12px 24px",
    borderRadius: "24px",
    border: "none",
    background: VF.red,
    color: VF.white,
    cursor: "pointer",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
    height: "44px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  assignBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    background: VF.grey300,
  },

  tablewrp: {
    maxHeight: '500px',
    overflowY: 'auto'
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",
    borderRadius: "8px",
    // overflow: "hidden",
    border: `1px solid ${VF.border}`,
  },

  tableHead: {
    background: VF.grey900,
    color: VF.white,
  },

  th: {
    fontFamily: "VodafoneRg-Bold, sans-serif",
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "14px 16px",
    textAlign: "left",
    color: VF.white,
  },

  td: {
    padding: "14px 16px",
    fontSize: "0.875rem",
    color: VF.grey700,
    borderBottom: `1px solid ${VF.borderLight}`,
    verticalAlign: "middle",
  },

  rowEven: {
    background: VF.white,
  },

  rowOdd: {
    background: VF.grey50,
  },

  emailTag: {
    display: "inline-block",
    background: VF.grey100,
    border: `1px solid ${VF.border}`,
    padding: "3px 10px",
    borderRadius: "14px",
    fontSize: "0.8rem",
    marginBottom: "4px",
    marginRight: "4px",
    color: VF.grey700,
  },

  moreLink: {
    color: VF.red,
    cursor: "pointer",
    fontFamily: "VodafoneRg-Bold, sans-serif",
    fontSize: "0.8rem",
    marginTop: "4px",
    display: "inline-block",
  },

  noSme: {
    color: VF.grey300,
    fontStyle: "italic",
    fontSize: "0.8rem",
  },

  manageBtn: {
    fontFamily: "VodafoneRg-Bold, sans-serif",
    fontSize: "0.8rem",
    padding: "8px 16px",
    borderRadius: "20px",
    border: `1.5px solid ${VF.red}`,
    background: VF.white,
    color: VF.red,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(26,26,26,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    backdropFilter: "blur(2px)",
  },

  modalBox: {
    background: VF.white,
    padding: "28px 32px",
    borderRadius: "16px",
    width: "440px",
    maxWidth: "90vw",
    boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
    textAlign: "left",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
  },

  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `2px solid ${VF.red}`,
    paddingBottom: "12px",
    marginBottom: "16px",
  },

  modalTitle: {
    fontFamily: "VodafoneRg-Bold, sans-serif",
    fontSize: "1.15rem",
    color: VF.grey900,
    margin: 0,
  },

  modalCloseX: {
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    color: VF.grey500,
    cursor: "pointer",
    lineHeight: 1,
    padding: "4px",
  },

  modalBody: {
    flex: 1,
    overflowY: "auto",
    paddingRight: "4px",
  },

  emailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    borderRadius: "8px",
    marginBottom: "6px",
    background: VF.grey50,
    border: `1px solid ${VF.borderLight}`,
    transition: "all 0.15s ease",
  },

  emailText: {
    fontSize: "0.875rem",
    color: VF.grey700,
    fontFamily: "VodafoneRg-Regular, sans-serif",
    wordBreak: "break-all",
    verticalAlign: "text-top",
    display: " inline-block"
  },

  deleteBtn: {
    fontFamily: "VodafoneRg-Bold, sans-serif",
    fontSize: "0.75rem",
    background: VF.white,
    border: `1.5px solid ${VF.red}`,
    borderRadius: "6px",
    cursor: "pointer",
    padding: "5px 12px",
    color: VF.red,
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },

  deleteAllBtn: {
    fontFamily: "VodafoneRg-Bold, sans-serif",
    fontSize: "0.8rem",
    background: VF.red,
    border: "none",
    borderRadius: "20px",
    cursor: "pointer",
    padding: "8px 16px",
    color: VF.white,
    marginBottom: "12px",
    alignSelf: "flex-end",
  },

  closeBtn: {
    marginTop: "16px",
    padding: "10px 24px",
    borderRadius: "24px",
    border: `1.5px solid ${VF.border}`,
    background: VF.white,
    color: VF.grey700,
    fontFamily: "VodafoneRg-Bold, sans-serif",
    fontSize: "0.875rem",
    cursor: "pointer",
    alignSelf: "center",
  },

  loadingWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 0",
    gap: "12px",
  },

  spinner: {
    width: "24px",
    height: "24px",
    border: `3px solid ${VF.grey100}`,
    borderTop: `3px solid ${VF.red}`,
    borderRadius: "50%",
    animation: "vf-spin 0.8s linear infinite",
  },

  emptyState: {
    textAlign: "center",
    padding: "48px 0",
    color: VF.grey300,
    fontFamily: "VodafoneRg-Regular, sans-serif",
    fontSize: "0.95rem",
  },

  ideaCount: {
    fontFamily: "VodafoneRg-Regular, sans-serif",
    fontSize: "0.8rem",
    color: VF.grey500,
    marginBottom: "8px",
  },

  confirmOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(26,26,26,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10001,
  },

  confirmBox: {
    background: VF.white,
    padding: "24px 28px",
    borderRadius: "12px",
    width: "380px",
    maxWidth: "85vw",
    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
    textAlign: "center",
  },

  confirmTitle: {
    fontFamily: "VodafoneRg-Bold",
    fontSize: "1.1rem",
    color: VF.grey900,
    marginBottom: "12px",
  },

  confirmText: {
    fontFamily: "VodafoneRg-Regular",
    fontSize: "0.95rem",
    color: VF.grey500,
    marginBottom: "20px",
    lineHeight:'1.5'
  },

  confirmActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
  },

  cancelBtn: {
    padding: "8px 20px",
    borderRadius: "6px",
    border: `1px solid ${VF.grey300}`,
    background: VF.white,
    color: VF.grey700,
    fontFamily: "VodafoneRg-Regular",
    fontSize: "0.9rem",
    cursor: "pointer",
  },

  confirmBtn: {
    padding: "8px 20px",
    borderRadius: "6px",
    border: "none",
    background: VF.red,
    color: VF.white,
    fontFamily: "VodafoneRg-Bold",
    fontSize: "0.9rem",
    cursor: "pointer",
  },

  confirmBtnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

const SmeMapperAssignTools = (props) => {
  const {
    challengeList,
    smeGroupList,
    taggedIdeaList,
    isLoadingIdeaList,
    getInnovateChallengeTaggedIdeaListFn,
    deleteAllAssignedEvaluatorIntoIdeaFn,
    deleteAssignedEvaluatorIntoIdeaFn,
    postInnovateIdeaAddBySMEGroupsFn,
    deleteEvaluatorFromChallengesAllIdeaFn
  } = props;

  const [challenge, setChallenge] = useState(null);
  const [group, setGroup] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeEmails, setActiveEmails] = useState([]);
  const [showModalManage, setShowModalManage] = useState(false);
  const [activeIdeaKey, setActiveIdeaKey] = useState(null);
  const [ideaSearch, setIdeaSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [confirmDeleteAllSelectedUser, setConfirmDeleteAllSelectedUser] = useState(false);
  const [isRemoveAllSelectedUserLoading, setIsRemoveAllSelectedUserLoading] = useState(false);
  const [isDeleteAllLoading, setIsDeleteAllLoading] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [errorPopup, setErrorPopup] = useState({
    open: false,
    message: "",
  });
  const [successPopup, setSuccessPopup] = useState({
    open: false,
    message: "",
  });

  const handleCheckboxChange = (email) => {
    setSelectedEmails((prev) => prev.includes(email)
      ? prev.filter((e) => e !== email) // Uncheck: remove from array
      : [...prev, email]               // Check: add to array
    );
  };

  const challengeOptions = useMemo(
    () =>
      (challengeList || []).map((c) => ({
        value: c.id,
        label: c.title || c.name || c.id,
      })),
    [challengeList]
  );

  const groupOptions = useMemo(
    () =>
      (smeGroupList || []).map((g) => ({
        value: g.id,
        label: g.name || g.id,
      })),
    [smeGroupList]
  );

  const filteredList = useMemo(() => {
    const validIdeas =
      taggedIdeaList?.filter(
        (item) => item.ideaName !== null && item.ideaName?.trim() !== ""
      ) || [];

    if (!ideaSearch.trim()) return validIdeas;

    const search = ideaSearch.toLowerCase();

    return validIdeas.filter(
      (item) =>
        item.ideaName?.toLowerCase().includes(search) ||
        item.userEmailList?.some((email) =>
          email.toLowerCase().includes(search)
        )
    );
  }, [taggedIdeaList, ideaSearch]);

  useEffect(() => {
    if (challenge?.value) {
      getInnovateChallengeTaggedIdeaListFn(challenge.value);
    }
  }, [challenge]); // eslint-disable-line react-hooks/exhaustive-deps

  const findNameBasedOnGroupId = smeGroupList?.find(
    (matchId) => matchId?.id === group?.value
  );

  const activeIdea = taggedIdeaList?.find(
    (idea) => idea.ideaIdKey === activeIdeaKey
  );

  const isAssignDisabled = !group?.value || !challenge?.value;

  const assignSelectedToSME = async () => {
    const addParamsObj = {
      challengeKey: challenge?.value,
      businessSCFlag: "E",
      groupName: findNameBasedOnGroupId?.name,
      groupId: findNameBasedOnGroupId?.id,
    };

    await postInnovateIdeaAddBySMEGroupsFn(addParamsObj);
  };

  const openSMEPopup = (emails) => {
    setActiveEmails(emails);
    setShowModal(true);
  };

  const handleManageClick = (ideaKey) => {
    setActiveIdeaKey(ideaKey);
    setShowModalManage(true);
  };

  const closeManagePopup = () => {
    setShowModalManage(false);
    setConfirmDelete(null);
    setConfirmDeleteAll(false);
    setConfirmDeleteAllSelectedUser(false);
  };

  const refreshIdeaList = () => {
    if (challenge?.value) {
      getInnovateChallengeTaggedIdeaListFn(challenge.value);
    }
  };

  const deleteSME = (allParams, selectedEmail) => {
    const ideaIdKey = allParams?.ideaIdKey || activeIdeaKey;

    if (!ideaIdKey) {
      console.error("Cannot delete SME: ideaIdKey is null");
      return;
    }

    setConfirmDelete({
      email: selectedEmail,
      ideaIdKey,
    });
  };

  const confirmDeleteSME = () => {
    if (!confirmDelete) return;

    const { ideaIdKey, email } = confirmDelete;

    deleteAssignedEvaluatorIntoIdeaFn(
      {
        ideaIdKey,
        userEmail: email,
        businessSCFlag: "E",
        challengeKey: challenge?.value,
      },
      () => {
        setConfirmDelete(null);
        // refreshIdeaList();
      },
      () => {
        setConfirmDelete(null);
      }
    );
  };

  const cancelDeleteSME = () => {
    setConfirmDelete(null);
  };

  const openDeleteAllConfirmation = () => {
    if (!activeIdea?.userEmailList?.length) return;
    setConfirmDeleteAll(true);
  };

  const openRemoveSelectedUserConfirmation = () => {
    if (!selectedEmails?.length) return;
    setConfirmDeleteAllSelectedUser(true);
  };

  const cancelRemoveSelectedUserConfirmation = () => {
    if (isRemoveAllSelectedUserLoading) return
    setConfirmDeleteAllSelectedUser(false);
  };

  const confirmDeleteAllSME = async () => {
    if (!activeIdea?.ideaIdKey) {
      setConfirmDeleteAll(false);
      return;
    }
    try {
      setIsDeleteAllLoading(true);
      await new Promise((resolve, reject) => {
        deleteAllAssignedEvaluatorIntoIdeaFn(
          {
            ideaIdKey: activeIdea.ideaIdKey,
            userEmail: activeIdea.userEmailList, // send complete list
            businessSCFlag: "E",
            challengeKey: challenge?.value,
          },
          resolve,
          reject
        );
      });

      // Close Popups
      setConfirmDeleteAll(false);
      setShowModalManage(false);
      setActiveIdeaKey(null);

      // Success Popup
      setSuccessPopup({
        open: true,
        message: "All evaluators removed successfully.",
      });

    } catch (error) {
      setErrorPopup({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to remove evaluators. Please try again.",
      });
    } finally {
      setIsDeleteAllLoading(false);
    }
  };

  const cancelDeleteAllSME = () => {
    if (isDeleteAllLoading) return;
    setConfirmDeleteAll(false);
  };

  const handleSelectedUserRemoveEvaluatorFromAllIdeaWhereTagWithChallenge = async () => {
    // Ensure there are actually checked values before making a request
    if (!selectedEmails || selectedEmails.length === 0) {
      console.log("No evaluators selected for removal.");
      setConfirmDeleteAllSelectedUser(false)
      return;
    }

    try {
      setIsRemoveAllSelectedUserLoading(true);
      const payloadParams = {
        challengeKey: challenge?.value,
        selectedUsers: selectedEmails,
        businessSCFlag: "E"
      };

      // 1. Execute your deletion prop function
      // Assuming this function accepts a callback or returns a Promise:

      await new Promise((resolve, reject) => {
        deleteEvaluatorFromChallengesAllIdeaFn(
          payloadParams,
          resolve,
          reject
        );
      });

      // Success Popup
      setSuccessPopup({
        open: true,
        message: "Successfully removed selected evaluators.",
      });

      // Success Callback: Clear checkbox state and fetch fresh data from parent
      setSelectedEmails([]);
      // Close Popups
      setConfirmDeleteAllSelectedUser(false)
      setShowModalManage(false);
      setActiveIdeaKey(null);




    } catch (error) {
      setErrorPopup({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to remove evaluators. Please try again.",
      });
    } finally {
      setIsRemoveAllSelectedUserLoading(false);
    }
  };





  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes vf-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .vf-manage-btn:hover {
            background: ${VF.red} !important;
            color: ${VF.white} !important;
          }

          .vf-assign-btn:hover:not(:disabled) {
            background: ${VF.redDark} !important;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(230,0,0,0.3);
          }

          .vf-delete-btn:hover {
            background: ${VF.red} !important;
            color: ${VF.white} !important;
          }

          .vf-delete-all-btn:hover {
            background: ${VF.redDark} !important;
          }

          .vf-email-row:hover {
            border-color: ${VF.red}44;
            background: ${VF.white};
          }

          .vf-table-row:hover td {
            background: #FFF5F5;
          }
        `}
      </style>

      <div style={styles.headerRow}>
        <h2 style={styles.heading}>SME Mapper – Assign Tools.
          <span className='icon-info tooltip_v' >
            <span title='This feature allows administrators to manage or remove Idea Evaluators for tagged ideas in the selected challenge. It does not support Double Scorecard Business Evaluators. Business Evaluators must be added or removed manually at the idea level using the Business Scorecard feature' className='tooltiptext_v'> This feature allows administrators to manage or remove Idea Evaluators for tagged ideas in the selected challenge. It does not support Double Scorecard Business Evaluators. Business Evaluators must be added or removed manually at the idea level using the Business Scorecard feature. </span>
          </span>
        </h2>

        {filteredList?.length > 0 && (
          <span style={styles.badge}>{filteredList.length} Ideas</span>
        )}
      </div>

      <div style={styles.filters}>
        <div style={styles.filterGroup}>
          <label htmlFor="challenge-select" style={styles.filterLabel}>
            Challenge
          </label>

          <Select
            inputId="challenge-select"
            value={challenge}
            onChange={(selected) => setChallenge(selected)}
            options={challengeOptions}
            placeholder="Search & select challenge..."
            isClearable
            isSearchable
            styles={selectCustomStyles}
            noOptionsMessage={() => "No challenges found"}
          />
        </div>

        <div style={styles.filterGroup}>
          <label htmlFor="group-select" style={styles.filterLabel}>
            Community / SME Group
          </label>

          <Select
            inputId="group-select"
            value={group}
            onChange={(selected) => setGroup(selected)}
            options={groupOptions}
            placeholder="Search & select community..."
            isClearable
            isSearchable
            styles={selectCustomStyles}
            noOptionsMessage={() => "No communities found"}
          />
        </div>

        <button
          type="button"
          className="vf-assign-btn"
          style={{
            ...styles.assignBtn,
            ...(isAssignDisabled ? styles.assignBtnDisabled : {}),
          }}
          disabled={isAssignDisabled}
          onClick={assignSelectedToSME}
        >
          ⚡ Auto-Assign SME
        </button>
      </div>

      {challenge?.value && (
        <div style={{ marginBottom: "16px" }}>
          <input
            type="text"
            value={ideaSearch}
            onChange={(e) => setIdeaSearch(e.target.value)}
            placeholder="🔍 Search ideas by name or assigned email..."
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: "8px",
              border: `1px solid ${VF.border}`,
              fontFamily: "VodafoneRg-Regular, sans-serif",
              fontSize: "0.9rem",
              outline: "none",
              boxSizing: "border-box",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = VF.red;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = VF.border;
            }}
          />

          {ideaSearch && (
            <p style={styles.ideaCount}>
              Showing {filteredList.length} of{" "}
              {taggedIdeaList?.filter(
                (idea) => idea.ideaName && idea.ideaName.trim() !== ""
              )?.length || 0}{" "}
              ideas
            </p>
          )}
        </div>
      )}

      {(() => {
        if (isLoadingIdeaList) {
          return (
            <div style={styles.loadingWrapper}>
              <div style={styles.spinner} />
              <span
                style={{
                  color: VF.grey500,
                  fontFamily: "VodafoneRg-Regular",
                }}
              >
                Loading ideas...
              </span>
            </div>
          );
        }

        if (!challenge?.value) {
          return (
            <div style={styles.emptyState}>
              Select a challenge to load ideas
            </div>
          );
        }

        if (filteredList?.length === 0) {
          return (
            <div style={styles.emptyState}>
              No ideas found {ideaSearch ? "matching your search" : "for this challenge"}
            </div>
          );
        }

        return (
          <div style={styles.tablewrp}><table style={styles.table}>
            <thead style={styles.tableHead}>
              <tr>
                <th style={styles.th}>Idea</th>
                <th style={styles.th}>Assigned SMEs</th>
                <th style={{ ...styles.th, textAlign: "center" }}>
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredList.map((idea, idx) => (
                <tr
                  key={idea?.id || idea?.ideaIdKey || idx}
                  className="vf-table-row"
                  style={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}
                >
                  <td style={styles.td}>
                    <strong style={{
                      fontFamily: "VodafoneRg-Bold",
                      display: 'inline-block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '200px'

                    }}>
                      {idea?.ideaName}
                    </strong>
                  </td>

                  <td style={styles.td}>
                    {idea?.userEmailList && idea.userEmailList.length > 0 ? (
                      <div>
                        {idea.userEmailList.slice(0, 2).map((email, i) => (
                          <span key={i} style={styles.emailTag}>
                            {email}
                          </span>
                        ))}

                        {idea.userEmailList.length > 2 && (
                          <span
                            onClick={() => openSMEPopup(idea.userEmailList)}
                            style={styles.moreLink}
                          >
                            +{idea.userEmailList.length - 2} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={styles.noSme}>No SME assigned</span>
                    )}
                  </td>

                  <td style={{ ...styles.td, textAlign: "center" }}>
                    <button
                      type="button"
                      className="vf-manage-btn"
                      style={styles.manageBtn}
                      onClick={() => handleManageClick(idea?.ideaIdKey)}
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        );
      })()}

      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div
            style={styles.modalBox}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Assigned SME List</h3>

              <button
                style={styles.modalCloseX}
                onClick={() => setShowModal(false)}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              {activeEmails?.map((email, i) => (
                <div key={i} className="vf-email-row" style={styles.emailRow}>
                  <span style={styles.emailText}>{email}</span>
                </div>
              ))}
            </div>

            <button style={styles.closeBtn} onClick={() => setShowModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {showModalManage && (
        <div
          style={styles.modalOverlay}
          onClick={() => {
            if (!confirmDelete && !confirmDeleteAll && !confirmDeleteAllSelectedUser) {
              setShowModalManage(false);
            }
          }}
        >
          <div
            style={styles.modalBox}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Manage SME Assignments</h3>

              <button
                style={styles.modalCloseX}
                onClick={closeManagePopup}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalHeader}>
              {(
                <button
                  type="button"
                  className="vf-delete-all-btn"
                  // style={styles.deleteAllBtn}
                  onClick={openRemoveSelectedUserConfirmation}
                  disabled={selectedEmails?.length <= 0}
                  style={{
                    ...styles.deleteAllBtn,
                    // Optional visual cue: dim the button when disabled
                    opacity: selectedEmails?.length <= 0 ? 0.5 : 1,
                    cursor: selectedEmails?.length <= 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Remove Selected Evaluators from All Ideas Tagged with This Challenge
                </button>
              )}

              {activeIdea?.userEmailList?.length > 0 && (
                <button
                  type="button"
                  className="vf-delete-all-btn"
                  // style={styles.deleteAllBtn}
                  onClick={openDeleteAllConfirmation}
                  disabled={selectedEmails?.length > 0}
                  style={{
                    ...styles.deleteAllBtn,
                    // Optional visual cue: dim the button when disabled
                    opacity: selectedEmails?.length > 0 ? 0.5 : 1,
                    cursor: selectedEmails?.length > 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Remove All Evaluators
                </button>
              )}
            </div>

            <div style={styles.modalBody}>
              {activeIdea?.userEmailList?.length > 0 ? (
                activeIdea.userEmailList.map((email, i) => {
                  // Check if this specific email's checkbox is selected
                  const isSelected = selectedEmails.includes(email);

                  return (
                    <div key={email} className="vf-email-row" style={styles.emailRow}>
                      <div className="block2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleCheckboxChange(email)}
                        />
                        <span style={styles.emailText}>{email}</span>
                      </div>

                      <button
                        type="button"
                        className="vf-delete-btn"
                        // style={styles.deleteBtn}
                        onClick={() => deleteSME(activeIdea, email)}
                        disabled={isSelected}
                        style={{
                          ...styles.deleteBtn,
                          // Optional visual cue: dim the button when disabled
                          opacity: isSelected ? 0.5 : 1,
                          cursor: isSelected ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Remove
                      </button>

                    </div>
                  )
                })
              ) : (
                <p style={styles.noSme}>No SMEs assigned to this idea</p>
              )}
            </div>

            <button style={styles.closeBtn} onClick={closeManagePopup}>
              Close
            </button>
          </div>


          {confirmDelete && (
            <div
              style={styles.confirmOverlay}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.confirmBox}>
                <h4 style={styles.confirmTitle}>Confirmation</h4>

                <p style={styles.confirmText}>
                  Are you sure you want to delete the evaluator{" "}
                  <strong style={{ color: VF.grey700 }}>
                    {confirmDelete.email}
                  </strong>{" "}
                  added to this idea?
                </p>

                <div style={styles.confirmActions}>
                  <button
                    type="button"
                    onClick={cancelDeleteSME}
                    style={styles.cancelBtn}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={confirmDeleteSME}
                    style={styles.confirmBtn}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          {confirmDeleteAll && (
            <div
              style={styles.confirmOverlay}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.confirmBox}>
                <h4 style={styles.confirmTitle}>Confirmation</h4>

                <p style={styles.confirmText}>
                  Are you sure you want to remove all evaluators from this idea?
                </p>

                <p
                  style={{
                    ...styles.confirmText,
                    marginTop: "-10px",
                    fontSize: "0.85rem",
                  }}
                >
                  Total evaluators:{" "}
                  <strong style={{ color: VF.grey700 }}>
                    {activeIdea?.userEmailList?.length || 0}
                  </strong>
                </p>

                <div style={styles.confirmActions}>
                  <button
                    type="button"
                    onClick={cancelDeleteAllSME}
                    disabled={isDeleteAllLoading}
                    style={{
                      ...styles.cancelBtn,
                      ...(isDeleteAllLoading ? styles.confirmBtnDisabled : {}),
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={confirmDeleteAllSME}
                    disabled={isDeleteAllLoading}
                    style={{
                      ...styles.confirmBtn,
                      ...(isDeleteAllLoading ? styles.confirmBtnDisabled : {}),
                    }}
                  >
                    {isDeleteAllLoading ? "Removing..." : "Remove All"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {confirmDeleteAllSelectedUser && (
            <div
              style={styles.confirmOverlay}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.confirmBox}>
                <h4 style={styles.confirmTitle}>Warring</h4>

                <p style={styles.confirmText}>
                  Are you sure you want to remove the selected evaluators from all ideas associated with this challenge? Once removed, their scorecards will no longer be available. Click Submit to confirm.
                  <strong style={{ color: VF.grey700 }}>
                    {confirmDeleteAllSelectedUser.email}
                  </strong>{" "}
                  
                </p>

                <div style={styles.confirmActions}>
                  <button
                    type="button"
                    onClick={cancelRemoveSelectedUserConfirmation}
                    disabled={isRemoveAllSelectedUserLoading}
                    style={{
                      ...styles.cancelBtn,
                      ...(isRemoveAllSelectedUserLoading ? styles.confirmBtnDisabled : {}),
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={handleSelectedUserRemoveEvaluatorFromAllIdeaWhereTagWithChallenge}
                    style={{
                      ...styles.confirmBtn,
                      ...(isRemoveAllSelectedUserLoading ? styles.confirmBtnDisabled : {}),
                    }}
                    disabled={isRemoveAllSelectedUserLoading}
                  >
                    {isRemoveAllSelectedUserLoading ? "Removing..." : "Confirm"}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      )}


      {successPopup.open && (
        <div style={styles.confirmOverlay}>
          <div style={styles.confirmBox}>
            <h4 style={styles.confirmTitle}>Success</h4>

            <p style={styles.confirmText}>
              {successPopup.message}
            </p>

            <button
              type="button"
              style={styles.confirmBtn}
              onClick={() =>
                setSuccessPopup({
                  open: false,
                  message: "",
                })
              }
            >
              OK
            </button>
          </div>
        </div>
      )}

      {errorPopup.open && (
        <div style={styles.confirmOverlay}>
          <div style={styles.confirmBox}>
            <h4
              style={{
                ...styles.confirmTitle,
                color: VF.red,
              }}
            >
              Error
            </h4>

            <p style={styles.confirmText}>
              {errorPopup.message}
            </p>

            <button
              type="button"
              style={styles.confirmBtn}
              onClick={() =>
                setErrorPopup({
                  open: false,
                  message: "",
                })
              }
            >
              OK
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default SmeMapperAssignTools;